import { useCallback, useEffect, useState } from "react";
import type {
  ColumnOrderState,
  ColumnSizingState,
  OnChangeFn,
  VisibilityState,
} from "@tanstack/react-table";

/**
 * 一覧テーブルの「見た目の設定」を画面ごとに永続化する。
 *
 * 表示列・列順・列幅は担当者ごとに好みが分かれ、リロードで消えると使い物に
 * ならないため localStorage に保存する（ngtg_msc が SharedPreferences に
 * 保存しているのと同じ意図）。ページ番号・並び替え・絞り込みは URL の
 * クエリに持たせるので、ここでは扱わない。
 *
 * 保存するのは「非表示にした列」ではなく可視状態そのものだが、未知のキーは
 * 読み込み時に捨てるため、列が増減しても壊れない。
 *
 * 列数の多い画面では、使う場面が限られる列を `defaultHiddenColumnIds` で既定
 * 非表示にできる。保存済みの設定は既定より優先されるので、**一度でも自分で
 * 切り替えた列はその選択が残り、まだ見たことのない列だけが既定に従う**。
 */
export interface TableSettings {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
  columnSizing: ColumnSizingState;
}

/** 既定の設定。既定非表示の列だけ false を置く。 */
function defaults(defaultHiddenColumnIds: string[]): TableSettings {
  return {
    columnVisibility: Object.fromEntries(
      defaultHiddenColumnIds.map((id) => [id, false]),
    ),
    columnOrder: [],
    columnSizing: {},
  };
}

const storageKey = (tableKey: string) => `table-settings:${tableKey}`;

/**
 * 固定列を必ず先頭に寄せる。
 *
 * 固定列は「先頭から連続していること」を前提に左からの位置を積み上げるため
 * （[[DataTable]]）、並べ替えの結果どこへ移動しても正規化しておく。
 */
function normalizeOrder(
  order: ColumnOrderState,
  pinnedColumnIds: string[],
): ColumnOrderState {
  if (order.length === 0 || pinnedColumnIds.length === 0) return order;
  const pinned = new Set(pinnedColumnIds);
  return [
    ...order.filter((id) => pinned.has(id)),
    ...order.filter((id) => !pinned.has(id)),
  ];
}

function load(
  tableKey: string,
  knownColumnIds: string[],
  pinnedColumnIds: string[],
  defaultHiddenColumnIds: string[],
): TableSettings {
  const base = defaults(defaultHiddenColumnIds);
  try {
    const raw = localStorage.getItem(storageKey(tableKey));
    if (!raw) return base;
    const parsed = JSON.parse(raw) as Partial<TableSettings>;
    const known = new Set(knownColumnIds);
    // 列定義から消えたキーは捨てる（古い設定が残って表示が壊れるのを防ぐ）。
    const pick = <T extends Record<string, unknown>>(obj: T | undefined) =>
      Object.fromEntries(Object.entries(obj ?? {}).filter(([id]) => known.has(id)));
    return {
      // 既定を下敷きにして保存済みを重ねる。あとから足した既定非表示の列は、
      // 保存済みの設定に入っていないので既定（非表示）のままになる。
      columnVisibility: {
        ...base.columnVisibility,
        ...pick(parsed.columnVisibility),
      } as VisibilityState,
      columnSizing: pick(parsed.columnSizing) as ColumnSizingState,
      columnOrder: normalizeOrder(
        (parsed.columnOrder ?? []).filter((id) => known.has(id)),
        pinnedColumnIds,
      ),
    };
  } catch {
    // 壊れた JSON やプライベートモードでの失敗は既定値で続行する。
    return base;
  }
}

export function useTableSettings(
  tableKey: string,
  knownColumnIds: string[],
  pinnedColumnIds: string[] = [],
  defaultHiddenColumnIds: string[] = [],
) {
  const [settings, setSettings] = useState<TableSettings>(() =>
    load(tableKey, knownColumnIds, pinnedColumnIds, defaultHiddenColumnIds),
  );

  useEffect(() => {
    try {
      localStorage.setItem(storageKey(tableKey), JSON.stringify(settings));
    } catch {
      // 保存に失敗しても操作は続けられるようにする（プライベートモード等）。
    }
  }, [tableKey, settings]);

  // TanStack Table の OnChangeFn は「値」または「更新関数」を渡してくる。
  const makeHandler = <K extends keyof TableSettings>(
    key: K,
  ): OnChangeFn<TableSettings[K]> =>
    ((updater) =>
      setSettings((prev) => ({
        ...prev,
        [key]:
          typeof updater === "function"
            ? (updater as (old: TableSettings[K]) => TableSettings[K])(prev[key])
            : updater,
      }))) as OnChangeFn<TableSettings[K]>;

  // 既定に戻す（既定非表示の列は非表示に戻る）。
  const reset = useCallback(
    () => setSettings(defaults(defaultHiddenColumnIds)),
    // 配列リテラルで渡されることが多いので、中身で比較する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultHiddenColumnIds.join(",")],
  );

  const setColumnOrder: OnChangeFn<ColumnOrderState> = (updater) =>
    setSettings((prev) => ({
      ...prev,
      columnOrder: normalizeOrder(
        typeof updater === "function" ? updater(prev.columnOrder) : updater,
        pinnedColumnIds,
      ),
    }));

  return {
    settings,
    setColumnVisibility: makeHandler("columnVisibility"),
    setColumnOrder,
    setColumnSizing: makeHandler("columnSizing"),
    /** 表示列・列順・列幅をすべて既定に戻す。 */
    reset,
  };
}
